import { type BaseSyntheticEvent, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import {
  type FieldComponentProps,
  type FormField,
  FieldComponent as LibFieldComponent,
  SubmitButton as LibSubmitButton,
  SubmitMessage as LibSubmitMessage,
  useEmitFormSubmitEvent,
  useFormSelector,
  useGetCountries,
  useRegisterFormService,
  useWidgetSelector
} from '@elfsight/widget-form-builder/widget';
import { Loader as LibLoader, Typography } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { useFormPopup } from '../form-popup-context';
import { useInvalidFieldErrorMessageGetter } from '../utils';

interface FormLayoutProps {
  fields: FormField[];
  displayedFields: FormField[];
  onSubmit: (e: BaseSyntheticEvent) => Promise<string | undefined>;
}

export function FormLayout({
  fields,
  displayedFields,
  onSubmit
}: FormLayoutProps) {
  const { t } = useLingui();
  const { countries } = useGetCountries();

  const getInvalidFieldErrorMessage = useInvalidFieldErrorMessageGetter();

  const { language } = useSettings();

  const [error, setError] = useState<string>();
  const { submitted } = useWidgetSelector();
  const { status = '', fieldsError } = useFormSelector();
  const emitSubmitEvent = useEmitFormSubmitEvent();

  const {
    closePopup,
    onFormChange,
    formSettings: {
      submitButtonText,
      submitAction,
      submitMessageTitle,
      submitMessageText,
      submitMessageButton,
      submitMessageButtonText,
      recaptcha
    }
  } = useFormPopup();

  useRegisterFormService(fields);

  const handleSubmit = async (e: BaseSyntheticEvent) => {
    const submitError = await onSubmit(e);
    if (!submitError) {
      emitSubmitEvent(fields);
    }
  };

  useEffect(() => {
    if (status === 'failed') {
      setError(t`An error occurred when submitting the form, please try again`);
      return;
    }

    const { required, invalid } = fieldsError;
    if (required) {
      setError(t`Please fill all the required fields`);
      return;
    }

    if (invalid) {
      setError(t`Please fill all the fields correctly`);
      return;
    }

    setError(undefined);
  }, [status, fieldsError, t]);

  if (submitAction === 'message' && submitted && status === 'success') {
    return (
      <SubmitMessage
        fields={displayedFields}
        submitMessageTitle={submitMessageTitle}
        submitMessageText={submitMessageText}
        submitMessageButton={submitMessageButton}
        submitMessageButtonText={submitMessageButtonText}
        onSubmitMessageButtonClick={closePopup}
        fullWidth
      />
    );
  }

  return (
    <Container onSubmit={(event) => event.preventDefault()}>
      {displayedFields.map((field) => (
        <FieldComponent
          key={field.id}
          field={field}
          widgetLanguage={language.language}
          invalidErrorMessage={getInvalidFieldErrorMessage(field)}
          onChange={onFormChange}
          componentFieldProps={{
            imageChoice: {
              withInitialValue: false
            },
            phone: {
              countries
            }
          }}
          shadow
        />
      ))}

      <Show when={status !== 'pending'} fallback={<Loader />}>
        <SubmitButtonContainer>
          <SubmitButton text={submitButtonText} onClick={handleSubmit} />
        </SubmitButtonContainer>
      </Show>

      <Show when={['failed', 'invalid'].includes(status) && !!error}>
        <ErrorsGroup>
          <Error>{error}</Error>
        </ErrorsGroup>
      </Show>

      <Show when={recaptcha}>
        <Recaptcha
          dangerouslySetInnerHTML={{
            __html: t`This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy" target="_blank" rel="nofollow noreferrer noopener">Privacy Policy</a> and <a href="https://policies.google.com/terms" target="_blank" rel="nofollow noreferrer noopener">Terms of Service</a> apply.`
          }}
        />
      </Show>
    </Container>
  );
}

export const getJustifyContent = () => {
  return css`
    justify-content: ${({
      theme: {
        form: { submitButtonAlignment }
      }
    }) =>
      submitButtonAlignment === 'right' ? 'flex-end' : submitButtonAlignment};
  `;
};

const Container = styled.form.attrs({
  className: createReadableClass({ component: 'form', element: 'container' })
})``;

const Error = styled(Typography).attrs(
  ({
    theme: {
      form: { errorColor }
    }
  }) => ({
    className: createReadableClass({ component: 'form', element: 'error' }),
    color: errorColor,
    textStyle: {
      fontSize: 14
    }
  })
)``;

const ErrorsGroup = styled.div.attrs({
  className: createReadableClass({ component: 'form', element: 'errors-group' })
})`
  display: flex;
  margin-top: 12px;
  ${getJustifyContent};
`;

const SubmitMessage = styled(LibSubmitMessage).attrs(
  ({
    theme: {
      form: {
        submitMessageTitleFontStyle,
        submitMessageTextFontStyle,
        submitMessageButtonColor,
        submitMessageAlignment,
        submitMessageTextColor,
        submitMessageTitleColor,
        submitButtonProps: { borderRadius: submitMessageButtonBorderRadius }
      }
    }
  }) => ({
    submitMessageAlignment,
    submitMessageTextColor,
    submitMessageTitleColor,
    submitMessageTitleFontStyle,
    submitMessageTextFontStyle,
    submitMessageButtonColor,
    submitMessageButtonBorderRadius,
    className: createReadableClass({
      component: 'form',
      element: 'submit-message'
    })
  })
)``;

const Loader = styled(LibLoader).attrs(
  ({
    theme: {
      form: { fieldActiveColor }
    }
  }) => ({
    color: fieldActiveColor,
    className: createReadableClass({ component: 'form', element: 'loader' })
  })
)``;

const FieldComponent = styled(LibFieldComponent).attrs(
  ({
    theme: {
      form: { errorColor, ...formSettings }
    }
  }) => ({
    formSettings,
    errorColor,
    className: createReadableClass({ component: 'form', element: 'field' })
  })
)<Partial<FieldComponentProps>>`
  padding: 0;
`;

const SubmitButtonContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'form',
    element: 'submit-button-container'
  })
})`
  display: flex;
  ${getJustifyContent};
`;
const SubmitButton = styled(LibSubmitButton).attrs(
  ({
    theme: {
      form: {
        submitButtonProps: { text: _text, size: _size, ...submitButtonProps }
      }
    }
  }) => ({
    ...submitButtonProps,
    className: createReadableClass({
      component: 'form',
      element: 'submit-button'
    })
  })
)``;

const Recaptcha = styled(Typography).attrs(
  ({
    theme: {
      form: { widgetFooterTextColor, widgetFooterTextStyle }
    }
  }) => ({
    className: createReadableClass({
      component: 'form',
      element: 'recaptcha'
    }),
    textStyle: widgetFooterTextStyle,
    color: widgetFooterTextColor
  })
)`
  margin-top: 20px;

  & a {
    text-decoration: underline;
    cursor: pointer;
    color: ${({
      theme: {
        form: { widgetFooterLinksColor }
      }
    }) => widgetFooterLinksColor};
  }
`;
